import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { UntypedFormControl, Validators } from '@angular/forms';
import {
  AiService,
  FlashCardStacksService,
  FlashCardsService,
} from 'src/app/api/services';
import { PanelService } from 'src/app/cms/panel/panel.service';
import { Router } from '@angular/router';
import {
  AiFlashCardProposal,
  AiFlashCardQuery,
  FlashCardModel,
} from 'src/app/api/models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PanelEntityType } from 'src/app/cms/panel/panel.types';
import { ContentSpinnerComponent } from '../../ui/content-spinner.component';
import { MatIcon } from '@angular/material/icon';
import { NgTemplateOutlet } from '@angular/common';
import { TagInputComponent } from '../../form-field/tag-input.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';

@Component({
  selector: 'qa-import-ai-flashcard',
  templateUrl: './import-ai-flashcard.component.html',
  styleUrls: ['./import-ai-flashcard.component.scss'],
  imports: [
    ContentSpinnerComponent,
    MatDialogClose,
    MatIcon,
    NgTemplateOutlet,
    TagInputComponent,
    ButtonComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    TranslateModule,
  ],
})
export class ImportAiFlashcardComponent implements OnInit {
  showInfo = true;
  showGeneratedStack = false;
  generatedStackResult: AiFlashCardProposal;
  keywords = new UntypedFormControl(null);
  loading = false;
  course: any;
  stack: any;
  creatingCard = false;

  constructor(
    public dialogRef: MatDialogRef<ImportAiFlashcardComponent>,
    private matSnackBar: MatSnackBar,
    private router: Router,
    private aiService: AiService,
    private flashCards: FlashCardsService,
    private flashCardStacks: FlashCardStacksService,
    private panels: PanelService,
    private translator: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    const routeParts = this.router.url.split('/');
    const idPart = routeParts[routeParts.length - 1];
    const idParts = idPart.split(';');
    const sPanel = this.panels.panels.find(
      (p) => p.type === PanelEntityType.FlashCardStack
    );
    if (routeParts && idPart && idParts.length > 1) {
      const cId = parseInt(idParts[1].split('=')[1]);
      const sId = parseInt(idParts[2].split('=')[1]);
      const cPanel = this.panels.panels.find(
        (p) => p.type === PanelEntityType.Course
      );
      const course = cPanel.list.find((c) => c.id === cId);
      if (course) {
        this.course = course;
      }
      const stack = sPanel.list.find((c) => c.id === sId);
      if (stack) {
        this.stack = stack;
      }
    } else if (routeParts.includes('flash-cards')) {
      const stackId = parseInt(routeParts[routeParts.length - 2]);
      const stack = sPanel.list.find((c) => c.id === stackId);
      if (stack) {
        this.stack = stack;
      } else {
        this.getFlashCardStackById(stackId);
      }
    } else {
      this.keywords.setValidators(Validators.required);
      this.keywords.updateValueAndValidity();
    }
  }

  generateStackAICard() {
    this.loading = true;
    this.showInfo = false;
    this.showGeneratedStack = true;
    this.generateCard();
  }

  generateCard() {
    const obj: AiFlashCardQuery = {
      category: null,
      keywords: '',
      stack_id: this.stack?.id,
      stack_name: this.stack?.name,
    };
    if (this.course) {
      obj.course_name = this.course?.name;
      obj.category = this.course?.category?.name || null;
    }
    if (this.keywords.value) {
      const keywords = this.keywords.value.map((v) => v.name).join(', ');
      obj.keywords = keywords;
    }
    this.aiService
      .generateFlashCard({
        body: obj,
      })
      .subscribe((data) => {
        this.generatedStackResult = data[0];
        this.loading = false;
      });
  }

  importAICard() {
    this.creatingCard = true;
    let request$;
    if (this.course) {
      request$ = this.flashCardStacks.copyCardsToFlashCardStack({
        id: this.stack.id,
        body: [this.buildRequest()],
      });
    } else {
      request$ = this.flashCards.createFlashCard({ body: this.buildRequest() });
    }
    request$.subscribe(() => {
      this.creatingCard = false;
      this.dialogRef.close({ refresh: true });
      this.matSnackBar.open(
        this.translator.instant('StackAIQuestion.successToast')
      );
    });
  }

  generateAICardAgain() {
    this.loading = true;
    this.generateCard();
  }

  getFlashCardStackById(stackId: number) {
    this.flashCardStacks
      .getFlashCardStackById({ id: stackId })
      .subscribe((stack) => {
        this.stack = stack;
      });
  }

  saveCardAndNext() {
    this.creatingCard = true;
    let request$;
    if (this.course) {
      request$ = this.flashCardStacks.copyCardsToFlashCardStack({
        id: this.stack.id,
        body: [this.buildRequest()],
      });
    } else {
      request$ = this.flashCards.createFlashCard({ body: this.buildRequest() });
    }
    request$.subscribe(() => {
      this.creatingCard = false;
      this.showInfo = true;
      this.showGeneratedStack = false;
      this.matSnackBar.open(
        this.translator.instant('StackAIQuestion.successToast')
      );
    });
  }

  buildRequest(): FlashCardModel {
    const card = this.generatedStackResult;
    return {
      text: card.question,
      explanation: card.explanation,
      answer: card.answer,
    };
  }

  retry() {
    this.showInfo = true;
    this.showGeneratedStack = false;
  }
}
